import { Tooltip } from "primereact/tooltip";
import { FabrixIcon, formatDate, humanFileSize } from "../../common";
import { FileUpload, FileUploadBeforeUploadEvent, FileUploadHeaderTemplateOptions, FileUploadOptions, FileUploadProps, FileUploadRemoveEvent, FileUploadSelectEvent, FileUploadUploadEvent, ItemTemplateOptions } from "primereact/fileupload";
import { createRef, CSSProperties, useEffect, useImperativeHandle, useState } from "react";
import useAuthentication from "../../hooks/use-authentication";
import { ApiService, JwtAuthService } from "../../services";
import { useIcon, useTranslation } from "../../hooks";
import React from "react";

export interface FileUploaderRef {
    clear: () => void;
}

export type UploadButton = "choose" | "upload" | "cancel"


export interface FileUploaderProps {

    maxFileSize?: number;
    accept?: string;
    onFileUploaded?: (result: any) => void;
    disabled?: boolean;
    uploadUrl?: string;
    mode?: "basic" | "advanced";
    onFileSelected?: (file: File) => Promise<void>
    onClear?: () => void
    buttons?: UploadButton[];
    style?: CSSProperties;
    className?: string;
    formData?: FormData;
}

const FileUploader = React.forwardRef((props: FileUploaderProps, ref) => {


    const isAuth = useAuthentication();
    const icon = useIcon();
    const translate = useTranslation();
    const [_token, setToken] = useState<string>();
    const [_filename, setFileName] = useState<string>('')
    const [_fileType, setFileType] = useState<string>(props.accept ?? '*/*');
    const [_file, setFile] = useState<File>();
    const [_uploadUrl, setUploadUrl] = useState<string>(props.uploadUrl ?? '')
    const _ref = createRef<FileUpload>()

    /**
     * Exposes imperative methods allowing parent components to clear uploads.
     */
    useImperativeHandle(ref, () => ({
        clear() {
            _ref.current?.clear();
        }
    }));

    /**
     * Resets internal file state and notifies consumers when the list is cleared.
     */
    const handleClear = () => {
        setFile(undefined);
        if (props.onClear)
            props.onClear()
    }

    /**
     * Captures the selected file and invokes the optional pre-upload handler.
     * @param e PrimeReact file selection event.
     */
    const handleOnSelect = async (e: FileUploadSelectEvent) => {
        if (e.files.length > 0) {
            let file = e.files[0];
            setFile(file);
            setFileName(file.name)
            if (props.onFileSelected) {
                await props.onFileSelected(file)

            }
        }
    }

    useEffect(() => { setUploadUrl(props.uploadUrl ?? '') }, [props.uploadUrl])

    useEffect(() => { setFileType(props.accept ?? '*/*') }, [props.accept])


    useEffect(() => {
        getToken().then(t => setToken(t))
        
    }, [])




    /**
     * Retrieves a JWT token silently for authenticated uploads when needed.
     * @returns Access token string (may be empty when unauthenticated).
     */
    const getToken = async () => {
        let token = "";

        if (isAuth) {
            let service = new JwtAuthService();
            token = await service.silentLogin();
        }
        return token;
    }


    /**
     * Injects authorization headers and extra form data before the upload request is sent.
     * @param e PrimeReact before-upload event.
     */
    const onBeforeUpload = (e: FileUploadBeforeUploadEvent) => {
        e.xhr.setRequestHeader("Authorization", `Bearer ${_token}`)
        if (props.formData) {
            for (const [key, value] of props.formData.entries()) {
                e.formData.append(key, value);
            }
        }
    }

    /**
     * Handles the upload completion response and informs subscribers.
     * @param e PrimeReact upload event containing the server response.
     */
    const handleUpload = (e: FileUploadUploadEvent) => {
        let text = e.xhr.responseText
        let result = ApiService.deserialize(text);
        if (props.onFileUploaded)
            props.onFileUploaded(result)
    }


    let maxSize = props.maxFileSize ? props.maxFileSize : 500000 * 1024;

    /**
     * Renders information about the selected file within the upload list.
     * @param file Uploaded file object.
     * @param options PrimeReact item template options.
     * @returns JSX snippet describing the file.
     */
    const itemTemplate = (file: Object, options: ItemTemplateOptions) => {
        let uploadedFile = file as File;
        let lastModifiedDate = new Date(uploadedFile.lastModified);
        return (
            <div className="grid fbx-file-upload-info" >
                <div className="col-4">
                    <label className="fbx-file-upload-label">{`${translate("FILE_NAME")}:`}</label><span>{uploadedFile.name}</span>
                </div>
                <div className="col-4">
                    <label className="fbx-file-upload-label">{`${translate("FILE_SIZE")}:`}</label><span>{humanFileSize(uploadedFile.size)}</span>
                </div>
                <div className="col-4">
                    <label className="fbx-file-upload-label">{`${translate("FILE_LAST_MODIFIED")}:`}</label><span>{formatDate(lastModifiedDate)}</span>
                </div>
            </div>
        )
    }

    /**
     * Displays placeholder text when no files are selected.
     */
    const emptyTemplate = (e: FileUploadProps) => {


        return (<p className="p-m-0">{translate("FILE_DRAG_DROP_FILE")}</p>)


    }

    useEffect(() => {
        if (!props.disabled) {
            const btn = document.querySelector('#fbx-file-uploader .custom-choose-btn') as HTMLElement | null;
            btn?.focus();
        }
    }, [props.disabled])



    /**
     * Customizes the uploader header to show optional button combinations.
     * @param options PrimeReact header template configuration.
     * @returns JSX fragment for the header buttons.
     */
    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
        const { className, chooseButton, uploadButton, cancelButton } = options;

        const showButton = (uploadButton: UploadButton) => {
            if (props.buttons) {
                return props.buttons.includes(uploadButton)
            }
            return true;
        }



        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center', }} >
                {showButton("choose") ? chooseButton : ''}
                {showButton("upload") ? uploadButton : ''}
                {showButton("cancel") ? cancelButton : ''}
            </div>
        )
    }

    const chooseOptions: FileUploadOptions = { icon: icon(FabrixIcon.add), iconOnly: true,  className: 'custom-choose-btn p-button-rounded p-button-text p-button-plain' };
    const uploadOptions: FileUploadOptions = { icon: icon(FabrixIcon.upload), iconOnly: true, className: 'custom-upload-btn p-button-rounded p-button-text p-button-plain' };
    const cancelOptions: FileUploadOptions = { icon: icon(FabrixIcon.cancel), iconOnly: true, className: 'custom-cancel-btn p-button-rounded p-button-text p-button-plain' };

    return (

        <div className={props.className} id="fbx-file-uploader" style={props.style}>

            <Tooltip target=".custom-choose-btn" content={translate("FILE_CHOOSE")} position="bottom" />
            <Tooltip target=".custom-upload-btn" content={translate("FILE_UPLOAD")} position="bottom" />
            <Tooltip target=".custom-cancel-btn" content={translate("FILE_CLEAR")} position="bottom" />

            <FileUpload ref={_ref} disabled={props.disabled} name="fileUpload" onClear={handleClear} onSelect={handleOnSelect} onBeforeSend={onBeforeUpload} url={_uploadUrl}
                onUpload={handleUpload} accept={_fileType} maxFileSize={maxSize} headerTemplate={headerTemplate} 
                itemTemplate={itemTemplate} emptyTemplate={emptyTemplate} className="" mode={props.mode} 
                chooseOptions={chooseOptions} uploadOptions={uploadOptions} cancelOptions={cancelOptions} />
        </div>




    )
})


export default FileUploader;
